import { dayWorkload } from "@japangolmok/core";
import { BottomSheet } from "../ui/BottomSheet";
import { useEffect, useState } from "react";
import {
	contentFreshness,
	googleMapsSearchUrl,
	recommendPlaces,
	recommendTripDays,
	type PlaceCandidate,
} from "@japangolmok/content";
import {
	appendTripStops,
	applyDayAdjustment,
	applyStopReplacement,
	previewStopReplacement,
	undoStopReplacement,
	previewDayAdjustment,
	removeTripPlanItem,
	moveTripPlanItem,
	undoDayAdjustment,
	type TripDay,
	type TripPlanItem,
	type TripStyle,
	type TripWorkspace,
} from "@japangolmok/core";
import { track } from "../analytics/events";
import { ExternalLink } from "../ui/Shell";

export type PersistTrip = (
	change: (draft: TripWorkspace) => void,
) => Promise<boolean | void>;
export const tasteOptions: { value: TripStyle; label: string }[] = [
	{ value: "food", label: "먹거리" },
	{ value: "culture", label: "문화·역사" },
	{ value: "nature", label: "자연" },
	{ value: "shopping", label: "가게 구경" },
	{ value: "family", label: "가족" },
	{ value: "slow", label: "천천히 걷기" },
];
export function candidateToStop(place: PlaceCandidate, courseId?: string) {
	return {
		id: place.legacyId,
		placeId: place.placeId,
		courseId,
		setting: place.setting,
		type: place.type,
		title: place.title,
		area: place.area,
		time: null,
		estimateJpy: place.estimateJpy,
		mapQuery: place.mapQuery,
		note: null,
		completed: false,
		role: place.role === "visit" ? ("optional" as const) : place.role,
		japaneseName: place.japaneseName ?? undefined,
		sourceUrl: place.sourceUrl,
		checkedAt: place.checkedAt ?? undefined,
		selectionReason: place.selectionReason,
		stayMinutes: place.stayMinutes ?? undefined,
	};
}

export function TripRecommendations({
	trip,
	persist,
}: {
	trip: TripWorkspace;
	persist: PersistTrip;
}) {
	const days = recommendTripDays(
		trip.destination,
		trip.styles,
		trip.days.length,
		trip.travelContext,
	);
	const [notice, setNotice] = useState("");
	const [busy, setBusy] = useState(false);
	const save = async (index?: number) => {
		setBusy(true);
		try {
			const ok = await persist((draft) => {
				days.forEach((day, i) => {
					if (index !== undefined && i !== index) return;
					const date = draft.days[i]?.date;
					if (date)
						Object.assign(
							draft,
							appendTripStops(
								draft,
								date,
								day.stops.map((place) => candidateToStop(place)),
							),
						);
				});
			});
			if (ok !== false) {
				setNotice(
					index === undefined
						? "추천을 날짜별 초안으로 담았어요. 같은 날 이미 담은 곳은 유지했어요."
						: `${index + 1}일차에 담았어요. 이미 담은 곳은 더하지 않았어요.`,
				);
				track("itinerary_template_added", {
					destinationAirport: trip.destination,
				});
			}
		} catch {
			setNotice(
				"추천을 저장하지 못했어요. 선택은 유지했으니 다시 담아 주세요.",
			);
		} finally {
			setBusy(false);
		}
	};
	return (
		<section className="trip-recommendations" aria-label="취향별 추천">
			<div className="section-heading">
				<div>
					<span className="eyebrow">이번에는 이렇게 보내요</span>
					<h3>취향을 고르면, 담을 곳도 달라져요.</h3>
				</div>
				<a href={`/walks?airport=${trip.destination}`}>
					동네의 반나절부터 보기 →
				</a>
			</div>
			<fieldset>
				<legend>
					더 보고 싶은 것{" "}
					<span className="optional">여러 개 선택 · 저장한 일정은 그대로</span>
				</legend>
				<div className="choice-chips">
					{tasteOptions.map(({ value, label }) => (
						<label key={value}>
							<input
								type="checkbox"
								checked={trip.styles.includes(value)}
								onChange={() =>
									void persist((draft) => {
										draft.styles = draft.styles.includes(value)
											? draft.styles.filter((style) => style !== value)
											: [...draft.styles, value];
									})
								}
							/>
							<span>{label}</span>
						</label>
					))}
				</div>
			</fieldset>
			<p className="form-trust">
				장소를 더 담기 전에 지도에서 이동을 확인해 주세요. 동네·메뉴 후보는 특정
				가게의 예약이나 영업 확인을 뜻하지 않아요.
			</p>
			<div className="recommendation-grid">
				{days.map((day, index) => (
					<article key={trip.days[index]?.date ?? index}>
						<span className="eyebrow">{index + 1}일차 제안</span>
						<h3>{day.title}</h3>
						<p>{day.reason}</p>
						<ol>
							{day.stops.map((stop) => (
								<li key={stop.placeId}>
									<strong>{stop.title}</strong>
									<p>{stop.selectionReason}</p>
									<small>
										{stop.role === "meal"
											? "식사 후보"
											: stop.role === "rest"
												? "쉬어 갈 후보"
												: "선택 방문"}{" "}
										· {stop.duration}
									</small>
								</li>
							))}
						</ol>
						{day.stops.length > 0 && (
							<button
								className="button secondary"
								type="button"
								disabled={busy}
								onClick={() => void save(index)}
							>
								{index + 1}일차에 담기
							</button>
						)}
					</article>
				))}
			</div>
			<button
				className="button primary"
				type="button"
				disabled={busy || !days.some((day) => day.stops.length)}
				onClick={() => void save()}
			>
				{busy ? "일정에 담는 중…" : "추천 일정 한 번에 담기"}
			</button>
			{notice && <p role="status">{notice}</p>}
		</section>
	);
}

const conditionLabels = {
	rain: "비가 와요",
	tired: "조금 지쳤어요",
	short_time: "시간이 줄었어요",
} as const;
export function DayAdjustment({
	trip,
	day,
	persist,
}: {
	trip: TripWorkspace;
	day: TripDay;
	persist: PersistTrip;
}) {
	const [preview, setPreview] = useState<ReturnType<
		typeof previewDayAdjustment
	> | null>(null);
	const [notice, setNotice] = useState("");
	const [busy, setBusy] = useState(false);
	const change = async (undo: boolean) => {
		setBusy(true);
		try {
			const ok = await persist((draft) =>
				Object.assign(
					draft,
					undo ? undoDayAdjustment(draft) : applyDayAdjustment(draft, preview!),
				),
			);
			if (ok !== false) {
				setPreview(null);
				setNotice(
					undo
						? "이 날의 변경을 되돌렸어요."
						: "확인한 변경을 저장했어요. 보류한 곳은 아래에 남겨 뒀어요.",
				);
				track("trip_day_adjusted", { surface: undo ? "undo" : "confirm" });
			}
		} catch {
			setNotice(
				"그동안 일정이 달라졌거나 저장하지 못했어요. 현재 일정을 보고 변경안을 다시 열어 주세요.",
			);
			setPreview(null);
		} finally {
			setBusy(false);
		}
	};
	const replaceable =
		preview &&
		day.items.find(
			(item) =>
				preview.changes.some((change) => change.itemId === item.id) &&
				!item.completed &&
				!item.locked &&
				!item.time &&
				!item.deferred,
		);
	return (
		<div id="day-adjustment" className="day-adjustment">
			<details>
				<summary>남은 일정 바꾸기</summary>
				<p>
					지금 상황을 직접 골라 주세요. 시간·꼭 갈 곳·완료한 곳은 남기고, 선택
					방문을 잠시 보류해요.
				</p>
				<div className="button-row">
					{Object.entries(conditionLabels).map(([condition, label]) => (
						<button
							className="button secondary"
							type="button"
							key={condition}
							disabled={busy}
							onClick={() => {
								setPreview(
									previewDayAdjustment(
										trip,
										day.date,
										condition as keyof typeof conditionLabels,
									),
								);
								setNotice("");
							}}
						>
							{label}
						</button>
					))}
				</div>
				<p className="form-trust">
					날씨나 영업을 자동 확인한 결과가 아니에요. 실내 대안과 이동시간은{" "}
					<a href={`/walks?airport=${trip.destination}`}>동네 안내</a>와 공식
					지도로 확인해 주세요.
				</p>
			</details>
			{preview && (
				<section className="adjustment-preview" aria-label="일정 변경 미리보기">
					<h4>{conditionLabels[preview.condition]} · 바꾸기 전에 확인해요</h4>
					{preview.changes.length ? (
						<ul>
							{preview.changes.map((change) => (
								<li key={change.itemId}>
									<strong>{change.title}</strong>
									<span>방문 예정 → 잠시 보류</span>
									<p>{change.reason}</p>
								</li>
							))}
						</ul>
					) : (
						<p>
							지금 보류할 선택 방문이 없어요. 고정한 일정과 완료한 곳은 그대로
							두었어요.
						</p>
					)}
					<p>변경해도 다른 날짜·시간 약속·원래 장소 기록은 남아요.</p>
					{replaceable && (
						<div className="condition-alternative">
							<h4>보류 대신 다른 한 곳을 고를까요?</h4>
							<p>
								같은 동네 후보를 먼저 보여요. 지도와 당일 영업을 확인한 뒤 한
								곳만 바꿔요.
							</p>
							<StopReplacement
								key={`${replaceable.id}-${preview.condition}`}
								trip={trip}
								day={day}
								item={replaceable}
								persist={persist}
								initialCondition={preview.condition}
								onApplied={() => {
									setPreview(null);
									setNotice("한 곳을 바꿨어요. 다음 장소를 확인해 주세요.");
									requestAnimationFrame(() => {
										const next =
											document.querySelector<HTMLElement>(".next-place");
										next?.focus();
										next?.scrollIntoView({ block: "start" });
									});
								}}
							/>
						</div>
					)}
					<div className="button-row">
						<button
							className="button primary"
							type="button"
							disabled={busy || !preview.changes.length}
							onClick={() => void change(false)}
						>
							이 변경 적용하기
						</button>
						<button
							className="button secondary"
							type="button"
							disabled={busy}
							onClick={() => {
								setPreview(null);
								setNotice("취소했어요. 일정은 바뀌지 않았어요.");
							}}
						>
							취소
						</button>
					</div>
				</section>
			)}
			{trip.lastAdjustment?.date === day.date && (
				<button
					className="button text"
					type="button"
					disabled={busy}
					onClick={() => void change(true)}
				>
					이 날의 마지막 변경 되돌리기
				</button>
			)}
			{notice && <p role="status">{notice}</p>}
		</div>
	);
}

function PlaceDetails({ item }: { item: TripPlanItem }) {
	const freshness = item.checkedAt
		? contentFreshness(item.checkedAt, new Date().toISOString())
		: "unknown";
	return (
		<>
			{item.japaneseName && (
				<p lang="ja" className="place-japanese">
					{item.japaneseName}
				</p>
			)}
			{item.selectionReason && <p>{item.selectionReason}</p>}
			{item.note && <p>{item.note}</p>}
			{item.returnReason && (
				<p className="return-reason">다음에도: {item.returnReason}</p>
			)}
			{item.stayMinutes != null && (
				<small>머무는 시간 약 {item.stayMinutes}분 · 이동시간 별도 확인</small>
			)}
			{item.sourceUrl && (
				<div className="place-source">
					<ExternalLink href={item.sourceUrl}>방문 조건 확인</ExternalLink>
					<small>
						{freshness === "stale"
							? "자료 재확인 필요"
							: freshness === "unknown"
								? "확인일 미확인"
								: `자료 검토 ${item.checkedAt?.slice(0, 10)}`}{" "}
						· 당일 영업 미확인
					</small>
				</div>
			)}
		</>
	);
}

export function DayPlan({
	trip,
	day,
	persist,
	today = false,
}: {
	trip: TripWorkspace;
	day: TripDay;
	persist: PersistTrip;
	today?: boolean;
}) {
	const [notice, setNotice] = useState("");
	const [actionId, setActionId] = useState<string | null>(null);
	const [editingId, setEditingId] = useState<string | null>(null);
	const ordered = [...day.items].sort(
		(a, b) => Number(Boolean(a.deferred)) - Number(Boolean(b.deferred)),
	);
	const next = ordered.find((item) => !item.completed && !item.deferred);
	const mutate = (id: string, change: (item: TripPlanItem) => void) =>
		persist((draft) => {
			const item = draft.days
				.find((d) => d.date === day.date)
				?.items.find((i) => i.id === id);
			if (item) change(item);
		});
	const saveItem = async (
		event: React.FormEvent<HTMLFormElement>,
		id: string,
	) => {
		event.preventDefault();
		const data = new FormData(event.currentTarget);
		try {
			const ok = await mutate(id, (item) => {
				item.time = String(data.get("time")) || null;
				item.locked = data.has("locked");
				item.note = String(data.get("note")).trim().slice(0, 500) || null;
				item.returnReason =
					String(data.get("returnReason")).trim().slice(0, 300) || undefined;
			});
			if (ok !== false) {
				setEditingId(null);
				setNotice("시간과 메모를 저장했어요.");
			}
		} catch {
			setNotice("저장하지 못했어요. 입력은 유지했으니 다시 저장해 주세요.");
		}
	};
	const workload = dayWorkload(trip, day);
	return (
		<div className="day-experience">
			<p className="day-workload">
				약 {Math.floor(workload.minutes / 60)}시간 {workload.minutes % 60}분 ·{" "}
				{workload.remaining >= 0
					? `여유 ${Math.floor(workload.remaining / 60)}시간`
					: day.date === trip.startDate
						? "도착일 일정이 많아요"
						: "이 날 일정이 많아요"}{" "}
				· 장소 사이 이동 20분 가정
				{workload.unknown > 0 && ` · 시간 모름 ${workload.unknown}곳`}
			</p>
			{today && next && (
				<section className="next-place" tabIndex={-1}>
					<span className="eyebrow">다음으로 할 일</span>
					<h2>{next.title}</h2>
					<div className="button-row">
						{next.mapQuery && (
							<ExternalLink
								className="button primary"
								href={googleMapsSearchUrl(next.mapQuery)}
								surface="today_map"
							>
								이곳 길찾기 열기
							</ExternalLink>
						)}
						<button
							className="button secondary"
							type="button"
							onClick={() =>
								void mutate(next.id, (item) => {
									item.completed = true;
								})
							}
						>
							다녀왔어요
						</button>
					</div>
					<details className="plan-item-details">
						<summary>이곳을 고른 이유와 방문 조건</summary>
						<PlaceDetails item={next} />
					</details>
					<a href="#day-adjustment" className="day-route-link">
						비·피로·시간에 맞춰 바꾸기 →
					</a>
				</section>
			)}
			{day.items.length === 0 ? (
				<p className="day-empty">
					아직 비어 있어요. 추천을 담거나 이 날을 여백으로 남겨도 좋아요.
				</p>
			) : (
				<>
					<p className="day-route-note">
						보이는 순서로 방문해요. 번호는 저장한 순서이며 이동시간·영업 확인은
						별도예요.
					</p>
					<ol className="plan-list experience-plan">
						{ordered.map((item) => (
							<li
								className={`${item.completed ? "is-done" : ""} ${item.deferred ? "is-deferred" : ""}`}
								key={item.id}
							>
								<button
									className="check-button"
									type="button"
									aria-label={`${item.title} ${item.completed ? "미완료로" : "완료로"}`}
									aria-pressed={item.completed}
									onClick={() =>
										void mutate(item.id, (target) => {
											target.completed = !target.completed;
										})
									}
								>
									{item.completed ? "✓" : ""}
								</button>
								<div className="plan-item-body">
									<span>
										{item.time ??
											(item.role === "meal"
												? "식사"
												: item.role === "rest"
													? "쉬어 가기"
													: "선택 방문")}{" "}
										· {item.area}
										{item.locked ? " · 꼭 갈 곳" : ""}
										{item.deferred ? " · 잠시 보류" : ""}
									</span>
									<div className="plan-item-title-row">
										<span className="plan-number" aria-hidden="true">
											{String(ordered.indexOf(item) + 1).padStart(2, "0")}
										</span>
										<strong>{item.title}</strong>
									</div>
									{trip.votes?.[item.id] && (
										<p>
											꼭 가요{" "}
											{
												trip.votes[item.id]!.filter((v) => v.value === "yes")
													.length
											}{" "}
											· 빼도 돼요{" "}
											{
												trip.votes[item.id]!.filter((v) => v.value === "skip")
													.length
											}
											{trip.votes[item.id]!.filter((v) => v.value === "skip")
												.length >
												trip.partySize / 2 && (
												<button
													type="button"
													onClick={() =>
														void persist((d) => {
															const selected = d.days.find(
																(x) => x.date === day.date,
															);
															if (selected) {
																selected.items = selected.items.filter(
																	(x) => x.id !== item.id,
																);
																d.candidates = [...(d.candidates ?? []), item];
																d.filmMoments?.forEach((m) => {
																	if (m.planItemId === item.id)
																		m.planItemId = null;
																});
															}
														})
													}
												>
													후보로 옮길까요?
												</button>
											)}
										</p>
									)}
									<button
										className="item-overflow"
										type="button"
										aria-label={`${item.title} 더보기`}
										onClick={() => setActionId(item.id)}
									>
										⋯
									</button>
									{actionId === item.id && (
										<BottomSheet
											title={item.title}
											onClose={() => setActionId(null)}
										>
											<details className="plan-item-details">
												<summary>이유·방문 조건·메모</summary>
												<PlaceDetails item={item} />
											</details>
											<div className="button-row">
												{item.mapQuery && (
													<ExternalLink
														href={googleMapsSearchUrl(item.mapQuery)}
														surface="map"
													>
														지도
													</ExternalLink>
												)}
												<button
													className="text-button"
													type="button"
													aria-expanded={editingId === item.id}
													onClick={() =>
														setEditingId(editingId === item.id ? null : item.id)
													}
												>
													시간·동행 메모
												</button>
												{item.deferred && (
													<button
														className="text-button"
														type="button"
														onClick={() =>
															void mutate(item.id, (target) => {
																target.deferred = false;
															})
														}
													>
														다시 일정에 넣기
													</button>
												)}
												<button
													className="text-button danger"
													type="button"
													onClick={async () => {
														try {
															if (item.locked) {
																setNotice(
																	"꼭 갈 곳으로 잠겨 있어요. 시간·동행 메모에서 잠금을 먼저 풀어 주세요.",
																);
																return;
															}
															await persist((draft) =>
																Object.assign(
																	draft,
																	removeTripPlanItem(draft, day.date, item.id),
																),
															);
														} catch {
															setNotice(
																"지우지 못했어요. 원래 일정은 남아 있어요.",
															);
														}
													}}
												>
													삭제
												</button>
											</div>
											{!item.completed && !item.locked && !item.time && (
												<ItemMoveControls
													trip={trip}
													day={day}
													item={item}
													persist={persist}
												/>
											)}
											{!item.completed &&
												!item.locked &&
												!item.time &&
												!item.deferred && (
													<StopReplacement
														trip={trip}
														day={day}
														item={item}
														persist={persist}
													/>
												)}
											{editingId === item.id && (
												<form
													className="editor-panel compact-form"
													onSubmit={(event) => void saveItem(event, item.id)}
												>
													<label>
														약속 시간{" "}
														<span className="optional">
															시간을 적으면 일정 조정에서 보호해요
														</span>
														<input
															name="time"
															type="time"
															defaultValue={item.time ?? ""}
														/>
													</label>
													<label className="inline-choice">
														<input
															name="locked"
															type="checkbox"
															defaultChecked={item.locked}
														/>{" "}
														꼭 갈 곳으로 남기기
													</label>
													<label>
														동행·합류 메모
														<input
															name="note"
															maxLength={500}
															defaultValue={item.note ?? ""}
															placeholder="예: 입구에서 만나기 · 동행이 고른 한 곳"
														/>
													</label>
													<label>
														다음에 다시 갈 이유
														<input
															name="returnReason"
															maxLength={300}
															defaultValue={item.returnReason ?? ""}
															placeholder="예: 다음에는 골목의 오래된 간판을 더 보고 싶어요"
														/>
													</label>
													<div className="button-row">
														<button className="button secondary" type="submit">
															메모 저장
														</button>
														<button
															className="button text"
															type="button"
															onClick={() => setEditingId(null)}
														>
															취소
														</button>
													</div>
												</form>
											)}
										</BottomSheet>
									)}
								</div>
							</li>
						))}
					</ol>
				</>
			)}
			{trip.lastReplacement?.date === day.date && (
				<button
					className="button text"
					type="button"
					onClick={async () => {
						try {
							const ok = await persist((draft) =>
								Object.assign(draft, undoStopReplacement(draft)),
							);
							if (ok !== false) setNotice("한 곳 교체를 되돌렸어요.");
						} catch {
							setNotice("그동안 일정이 달라졌어요. 현재 내용을 확인해 주세요.");
						}
					}}
				>
					마지막 한 곳 교체 되돌리기
				</button>
			)}
			<DayAdjustment trip={trip} day={day} persist={persist} />
			{notice && <p role="status">{notice}</p>}
		</div>
	);
}

function ItemMoveControls({
	trip,
	day,
	item,
	persist,
}: {
	trip: TripWorkspace;
	day: TripDay;
	item: TripPlanItem;
	persist: PersistTrip;
}) {
	const [notice, setNotice] = useState("");
	const [busy, setBusy] = useState(false);
	const [date, setDate] = useState(day.date);
	const index = day.items.findIndex((p) => p.id === item.id);
	const move = async (target: string, offset: number) => {
		setBusy(true);
		try {
			const ok = await persist((draft) =>
				Object.assign(
					draft,
					moveTripPlanItem(draft, day.date, item.id, target, offset),
				),
			);
			if (ok !== false) {
				setNotice(
					target === day.date
						? "방문 순서를 바꿨어요."
						: "고른 날짜로 옮겼어요. 메모와 사진도 함께 남아요.",
				);
				track("trip_section_open", { surface: "item_move" });
			}
		} catch {
			setNotice(
				"시간 약속·꼭 갈 곳·완료한 곳의 순서는 보호해요. 시간과 잠금을 먼저 확인해 주세요.",
			);
		} finally {
			setBusy(false);
		}
	};
	return (
		<details className="plan-item-details">
			<summary>순서·날짜 바꾸기</summary>
			<div className="plan-reorder">
				<button
					type="button"
					aria-label={item.title + " 먼저 보기"}
					disabled={busy || index === 0}
					onClick={() => void move(day.date, -1)}
				>
					↑ 먼저
				</button>
				<button
					type="button"
					aria-label={item.title + " 나중에 보기"}
					disabled={busy || index === day.items.length - 1}
					onClick={() => void move(day.date, 1)}
				>
					↓ 나중에
				</button>
			</div>
			{trip.days.length > 1 && (
				<div className="today-date-picker">
					<label>
						옮길 날짜
						<select value={date} onChange={(e) => setDate(e.target.value)}>
							{trip.days.map((d) => (
								<option value={d.date} key={d.date}>
									{d.date}
								</option>
							))}
						</select>
					</label>
					<button
						type="button"
						className="button secondary"
						disabled={busy || date === day.date}
						onClick={() => void move(date, 0)}
					>
						이 날짜로 옮기기
					</button>
				</div>
			)}
			{notice && <p role="status">{notice}</p>}
		</details>
	);
}

export function TripContextEditor({
	trip,
	persist,
}: {
	trip: TripWorkspace;
	persist: PersistTrip;
}) {
	const [notice, setNotice] = useState("");
	const [busy, setBusy] = useState(false);
	return (
		<details className="workspace-disclosure">
			<summary>숙소 동네·도착·귀국 시간에 맞춰 추천하기</summary>
			<p>
				예약정보를 가져오지 않아요. 알고 있는 동네와 현지 시각만 직접 적으면 새
				추천의 범위가 달라져요. 이미 저장한 일정은 바뀌지 않아요.
			</p>
			<form
				className="compact-form"
				onSubmit={async (event) => {
					event.preventDefault();
					const data = new FormData(event.currentTarget);
					setBusy(true);
					try {
						const ok = await persist((draft) => {
							draft.travelContext = {
								stayArea: String(data.get("stayArea") ?? "")
									.trim()
									.slice(0, 80),
								arrivalTime: String(data.get("arrivalTime") ?? "") || null,
								departureTime: String(data.get("departureTime") ?? "") || null,
							};
						});
						if (ok !== false)
							setNotice(
								"추천 조건을 이 기기에 저장했어요. 추천 일정에서 새 후보를 확인해 주세요.",
							);
					} catch {
						setNotice(
							"조건을 저장하지 못했어요. 입력은 남아 있으니 다시 저장해 주세요.",
						);
					} finally {
						setBusy(false);
					}
				}}
			>
				<label>
					숙소가 있는 동네 · 선택
					<input
						name="stayArea"
						maxLength={80}
						defaultValue={trip.travelContext?.stayArea ?? ""}
						placeholder="예: 하카타"
					/>
				</label>
				<div className="form-row">
					<label>
						첫날 도착 시각 · 선택
						<input
							name="arrivalTime"
							type="time"
							defaultValue={trip.travelContext?.arrivalTime ?? ""}
						/>
					</label>
					<label>
						마지막 날 귀국편 시각 · 선택
						<input
							name="departureTime"
							type="time"
							defaultValue={trip.travelContext?.departureTime ?? ""}
						/>
					</label>
				</div>
				<p className="form-trust">
					일본 현지 시각이에요. 공항 이동·수속 시간은 항공사와 직접 확인하고
					미리 비워 두세요.
				</p>
				<button type="submit" className="button secondary" disabled={busy}>
					{busy ? "저장 중…" : "추천 조건 저장"}
				</button>
			</form>
			{notice && <p role="status">{notice}</p>}
		</details>
	);
}

export function LocalSaveStatus({
	updatedAt,
	error,
}: {
	updatedAt: string;
	error?: string | null;
}) {
	const [online, setOnline] = useState(() => navigator.onLine);
	useEffect(() => {
		const sync = () => setOnline(navigator.onLine);
		window.addEventListener("online", sync);
		window.addEventListener("offline", sync);
		return () => {
			window.removeEventListener("online", sync);
			window.removeEventListener("offline", sync);
		};
	}, []);
	return (
		<div className="local-save-status" role={error ? "alert" : "status"}>
			{error ? (
				<strong>{error}</strong>
			) : (
				<>
					<strong>
						{online ? "이 기기에 저장한 여행" : "오프라인 · 마지막 확정본"}
					</strong>
					<span>
						저장{" "}
						{new Intl.DateTimeFormat("ko-KR", {
							dateStyle: "short",
							timeStyle: "short",
						}).format(new Date(updatedAt))}
						{!online
							? " · 외부 지도와 공식 안내는 연결 후 열 수 있어요."
							: " · 일정은 인터넷 없이도 다시 볼 수 있어요."}
					</span>
				</>
			)}
		</div>
	);
}

export function TripCandidates({
	trip,
	persist,
}: {
	trip: TripWorkspace;
	persist: PersistTrip;
}) {
	const [date, setDate] = useState(trip.startDate);
	const [notice, setNotice] = useState("");
	if (!trip.candidates?.length) return null;
	return (
		<section className="trip-candidates">
			<h3>아직 날짜를 정하지 않은 곳</h3>
			<p>
				짧아진 여행에 몰아넣지 않고 남겨 둔 후보예요. 보고 싶은 날을 직접
				골라요.
			</p>
			<label>
				담을 날짜
				<select value={date} onChange={(e) => setDate(e.target.value)}>
					{trip.days.map((day) => (
						<option value={day.date} key={day.date}>
							{day.date}
						</option>
					))}
				</select>
			</label>
			<ul>
				{trip.candidates.map((item) => (
					<li key={item.id}>
						<strong>{item.title}</strong>
						<p>{item.returnReason ?? item.selectionReason ?? item.note}</p>
						<button
							className="button secondary"
							type="button"
							onClick={async () => {
								const ok = await persist((draft) => {
									Object.assign(draft, appendTripStops(draft, date, [item]));
									draft.candidates = draft.candidates?.filter(
										(candidate) => candidate.id !== item.id,
									);
								});
								if (ok !== false)
									setNotice(
										"고른 날짜에 담았어요. 다른 후보는 그대로 남겼어요.",
									);
							}}
						>
							이 날짜에 담기
						</button>
					</li>
				))}
			</ul>
			{notice && <p role="status">{notice}</p>}
		</section>
	);
}

function StopReplacement({
	trip,
	day,
	item,
	persist,
	initialCondition = "normal",
	onApplied,
}: {
	trip: TripWorkspace;
	day: TripDay;
	item: TripPlanItem;
	persist: PersistTrip;
	initialCondition?: "normal" | "rain" | "tired" | "short_time";
	onApplied?: () => void;
}) {
	const [selected, setSelected] = useState("");
	const [preview, setPreview] = useState<ReturnType<
		typeof previewStopReplacement
	> | null>(null);
	const [notice, setNotice] = useState("");
	const [busy, setBusy] = useState(false);
	const [condition, setCondition] = useState(initialCondition);
	const neighborhood = (area: string) =>
		area.replace(/(?:역|주변|일대|권역|\s)/g, "").split(/[·・/]/)[0];
	const alternatives = recommendPlaces(trip.destination, trip.styles, {
		limit: 50,
	})
		.filter(
			(candidate) =>
				candidate.type === item.type &&
				(condition !== "rain" ||
					["indoor", "covered"].includes(candidate.setting)) &&
				(condition !== "tired" ||
					candidate.setting === "indoor" ||
					candidate.role === "rest" ||
					(candidate.stayMinutes !== null && candidate.stayMinutes <= 45)) &&
				(condition !== "short_time" ||
					(candidate.stayMinutes !== null && candidate.stayMinutes <= 45)) &&
				!day.items.some(
					(existing) =>
						existing.placeId === candidate.placeId ||
						existing.id === candidate.legacyId,
				),
		)
		.sort(
			(a, b) =>
				Number(neighborhood(b.area) === neighborhood(item.area)) -
				Number(neighborhood(a.area) === neighborhood(item.area)),
		);
	const candidate = alternatives.find(
		(alternative) => alternative.placeId === selected,
	);
	return (
		<details className="stop-replacement">
			<summary>이 한 곳을 다른 후보로 바꾸기</summary>
			<label>
				대안의 조건
				<select
					value={condition}
					onChange={(event) => {
						setCondition(event.target.value as typeof condition);
						setSelected("");
						setPreview(null);
					}}
				>
					<option value="normal">조건 없이 비교</option>
					<option value="rain">실내·지붕 있는 곳</option>
					<option value="tired">짧게 머물거나 쉬기</option>
					<option value="short_time">체류 45분 이내</option>
				</select>
			</label>
			{alternatives.length ? (
				<>
					<label>
						대신 들를 곳
						<select
							value={selected}
							onChange={(e) => {
								setSelected(e.target.value);
								setPreview(null);
								setNotice("");
							}}
						>
							<option value="">대안을 고르세요</option>
							{alternatives.map((alternative) => (
								<option value={alternative.placeId} key={alternative.placeId}>
									{alternative.title} · {alternative.area}
								</option>
							))}
						</select>
					</label>
					{candidate && (
						<>
							<p>{candidate.selectionReason}</p>
							<p>{candidate.caution}</p>
							<ExternalLink href={googleMapsSearchUrl(candidate.mapQuery)}>
								원래 동선에서 얼마나 이동하는지 확인
							</ExternalLink>
							<button
								className="button secondary"
								type="button"
								onClick={() => {
									try {
										setPreview(
											previewStopReplacement(
												trip,
												day.date,
												item.id,
												candidateToStop(candidate),
											),
										);
									} catch {
										setNotice(
											"일정 상태가 달라졌어요. 현재 장소와 고정 시간을 확인해 주세요.",
										);
									}
								}}
							>
								변경 전후 확인하기
							</button>
						</>
					)}
				</>
			) : (
				<p>
					아직 이 일정에 없는 같은 종류의 확인된 후보가 없어요. 새로 찾은 장소는
					아래의 직접 추가에서 적을 수 있어요.
				</p>
			)}
			{preview && (
				<section
					className="adjustment-preview"
					aria-label="한 곳 교체 미리보기"
				>
					<h4>
						{item.title} → {preview.replacementItem.title}
					</h4>
					<p>
						원래 장소는 잠시 보류로 남기고 새 장소를 그 자리에 담아요. 다른
						날짜와 기록은 그대로예요.
					</p>
					<div className="button-row">
						<button
							className="button primary"
							type="button"
							disabled={busy}
							onClick={async () => {
								setBusy(true);
								try {
									const ok = await persist((draft) =>
										Object.assign(draft, applyStopReplacement(draft, preview)),
									);
									if (ok !== false) {
										setPreview(null);
										setNotice(
											"한 곳을 바꿨어요. 원래 장소는 보류 목록에서 다시 볼 수 있어요.",
										);
										onApplied?.();
									}
								} catch {
									setPreview(null);
									setNotice(
										"현재 일정이 달라졌어요. 변경안을 다시 확인해 주세요.",
									);
								} finally {
									setBusy(false);
								}
							}}
						>
							이 한 곳 바꾸기
						</button>
						<button
							className="button secondary"
							type="button"
							disabled={busy}
							onClick={() => {
								setPreview(null);
								setNotice("취소했어요. 원래 일정은 그대로예요.");
							}}
						>
							바꾸지 않기
						</button>
					</div>
				</section>
			)}
			{notice && <p role="status">{notice}</p>}
		</details>
	);
}
